// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
// biome-ignore-all lint/correctness/useExhaustiveDependencies: TODO - address later
// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
import { useQuery } from "@tanstack/react-query";
import { useTypeWire } from "@typewirets/react";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { SystemId } from "@/components/ui/system-id";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type DeepResearchDataRow,
  useDeepResearchRowDetails,
} from "@/hooks/api/use-deep-research";
import { useExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import type { Entity } from "@/lib/api/entities.api";
import { EntitiesAPIWire } from "@/lib/api/entities.api.wire";
import { formatDate } from "@/lib/date-formatting";
import {
  getExternalConnectionLink,
  getExternalSystemName,
} from "@/lib/external-connection-links";
import { cn } from "@/lib/utils";
import type { DeepResearchReportMetadata } from "@/types/deep-research-reports";

interface DeepResearchDetailsSheetProps {
  report: DeepResearchReportMetadata;
  row: DeepResearchDataRow | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// Helper types for AI value shapes
type SingleValue = { single_value: string };
type ListValue = { list_value: string[] };

interface AICitationsItem {
  id?: string;
  field?: string;
  span?: string;
  justification?: string;
}

interface AIEnrichedValue {
  single_value?: string;
  list_value?: string[];
  citations?: AICitationsItem[];
  [key: string]: unknown;
}

function formatAIValue(value: unknown): string {
  if (value == null) return "-";
  const v = value as SingleValue | ListValue | string;
  if (typeof v === "string") return v;
  if (Object.hasOwn(v, "single_value")) {
    const sv = (v as SingleValue).single_value;
    return sv ?? "";
  }
  if (Object.hasOwn(v, "list_value")) {
    const lv = (v as ListValue).list_value;
    return Array.isArray(lv) ? lv.join(", ") : "";
  }
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

const ExpandableText = memo(
  ({ text, initialLines = 2 }: { text: string; initialLines?: number }) => {
    const [expanded, setExpanded] = useState(false);
    const shouldClamp = (text || "").length > 160;
    return (
      <div className="mt-2">
        <p
          className={cn(
            "text-[13px] leading-5 text-foreground/90",
            !expanded && shouldClamp && `line-clamp-${initialLines}`,
          )}
        >
          {text}
        </p>
        {shouldClamp && (
          <button
            type="button"
            className="mt-1 text-[11px] text-muted-foreground hover:text-foreground underline"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            aria-expanded={expanded}
          >
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
      </div>
    );
  },
);
ExpandableText.displayName = "ExpandableText";

// Virtualized citation list for performance with many items
interface VirtualizedCitationListProps {
  citations: AICitationsItem[];
  entities: Record<string, Entity>;
  externalConnections?: ReturnType<typeof useExternalConnectionLinks>["data"];
  fieldId: string;
}

const VirtualizedCitationList = memo(
  ({
    citations,
    entities,
    externalConnections,
    fieldId,
  }: VirtualizedCitationListProps) => {
    const [visibleCount, setVisibleCount] = useState(10);
    const loaderRef = useRef<HTMLDivElement>(null);

    // Use intersection observer for infinite scrolling
    useEffect(() => {
      if (!loaderRef.current) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setVisibleCount((prev) => {
              if (prev < citations.length) {
                return Math.min(prev + 10, citations.length);
              }
              return prev;
            });
          }
        },
        { threshold: 0.1 },
      );

      observer.observe(loaderRef.current);
      return () => observer.disconnect();
    }, [citations.length]);

    return (
      <div className="max-h-[600px] overflow-y-auto">
        {citations.slice(0, visibleCount).map((c, idx) => (
          <CitationCard
            key={`${fieldId}-${idx}`}
            citation={c}
            entity={entities[c.id || ""]}
            externalConnections={externalConnections}
          />
        ))}
        {visibleCount < citations.length && (
          <div ref={loaderRef} className="py-2 text-center">
            <span className="text-xs text-muted-foreground">
              Loading more... ({visibleCount} of {citations.length})
            </span>
          </div>
        )}
      </div>
    );
  },
);
VirtualizedCitationList.displayName = "VirtualizedCitationList";

// Memoized citation card to prevent unnecessary re-renders
interface CitationCardProps {
  citation: AICitationsItem;
  entity?: Entity;
  externalConnections?: ReturnType<typeof useExternalConnectionLinks>["data"];
}

const CitationCard = memo(
  ({ citation, entity, externalConnections }: CitationCardProps) => {
    const id = citation.id;
    if (!id) return <span className="text-foreground/80">—</span>;

    const prefix = id.includes("_") ? id.split("_", 1)[0] : undefined;
    const type = (entity?.type || prefix || "other").toLowerCase();
    const isTouchpoint = type === "email" || type === "meeting";

    // Type label for colored pill
    const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);

    // Neutral pills for details
    const dateLabel = entity?.timestamp
      ? formatDate(String(entity.timestamp))
      : "Unknown date";
    const titleLabel = entity?.name || "";

    // Badge variant by category
    const variantByType: Record<string, BadgeProps["variant"]> = {
      account: "category-account",
      opportunity: "category-opportunity",
      person: "category-person",
      campaign: "category-campaign",
      email: "category-touchpoint",
      meeting: "category-touchpoint",
      other: "category-other",
    };
    const badgeVariant = variantByType[type] || "category-other";

    // External link if we have source fields
    const url =
      entity?.source_record_id && entity?.source_record_type
        ? getExternalConnectionLink(
            {
              source_record_id: entity.source_record_id,
              source_record_type: entity.source_record_type,
            },
            externalConnections,
          )
        : null;

    return (
      <div className="rounded-md border p-2.5 bg-muted/30 hover:bg-muted/40 transition-colors">
        <div className="flex flex-col gap-1 min-w-[12rem]">
          <div className="flex items-center gap-2">
            <Badge variant={badgeVariant}>{typeLabel}</Badge>
            {isTouchpoint ? (
              <>
                <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-xs text-foreground/90">
                  {dateLabel}
                </span>
                {titleLabel && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-xs text-foreground/90">
                    {titleLabel}
                  </span>
                )}
              </>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-xs text-foreground/90">
                {entity?.name || id}
              </span>
            )}
            {url && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-muted-foreground hover:text-foreground"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    View in{" "}
                    {getExternalSystemName(entity?.source_record_type || "")}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex items-center gap-2">
            <SystemId id={id} />
            <span className="text-[11px] text-muted-foreground">
              {citation.field || "—"}
            </span>
          </div>
        </div>

        {citation.span && (
          <div className="mt-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
              Detail
            </div>
            <div className="text-[13px] text-foreground/80 bg-muted/30 rounded px-2 py-1">
              {citation.span}
            </div>
          </div>
        )}

        {citation.justification && (
          <div className="mt-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
              Justification
            </div>
            <ExpandableText text={citation.justification} />
          </div>
        )}
      </div>
    );
  },
);
CitationCard.displayName = "CitationCard";

function DeepResearchDetailsSheetContent({
  report,
  row,
  isOpen,
  onOpenChange,
}: DeepResearchDetailsSheetProps) {
  const { data: details } = useDeepResearchRowDetails(
    report?.id,
    row?.entity_id,
    { enabled: !!row?.entity_id && isOpen }, // Only fetch when open
  );

  // Resize handler refs to ensure cleanup on unmount/close
  const moveHandlerRef = useRef<((ev: MouseEvent) => void) | null>(null);
  const upHandlerRef = useRef<((ev: MouseEvent) => void) | null>(null);
  const isDraggingRef = useRef(false);

  const cleanupResizeListeners = () => {
    if (moveHandlerRef.current) {
      window.removeEventListener("mousemove", moveHandlerRef.current);
      moveHandlerRef.current = null;
    }
    if (upHandlerRef.current) {
      window.removeEventListener("mouseup", upHandlerRef.current);
      upHandlerRef.current = null;
    }
    isDraggingRef.current = false;
  };

  useEffect(() => {
    // Cleanup on unmount
    return () => cleanupResizeListeners();
  }, [cleanupResizeListeners]);

  useEffect(() => {
    // If the sheet closes mid-drag, remove listeners
    if (!isOpen && isDraggingRef.current) {
      cleanupResizeListeners();
    }
  }, [isOpen, cleanupResizeListeners]);

  const data = isOpen ? details?.data || row?.data || {} : {};
  const title = isOpen ? (data.name as string) || "Details" : "Details";

  // External link support if present on the entity
  const sourceRecordId = data.source_record_id as string | undefined;
  const sourceRecordType = data.source_record_type as string | undefined;
  const externalRecord =
    sourceRecordId && sourceRecordType
      ? {
          source_record_id: sourceRecordId,
          source_record_type: sourceRecordType,
        }
      : null;

  // Map AI field id to display name for sections
  const aiFieldIdToDisplayName = new Map<string, string>();
  report.ai_fields?.forEach((f) =>
    aiFieldIdToDisplayName.set(f.id, f.display_name),
  );

  // Memoize AI field entries to avoid recalculation
  const aiFieldEntries = useMemo(
    () => Object.entries(data).filter(([key]) => key.startsWith("aifd_")),
    [data],
  );

  // Memoize citation IDs extraction to prevent recalculation on every render
  const allCitationIds = useMemo(() => {
    if (!isOpen) return [];
    return Array.from(
      new Set(
        aiFieldEntries.flatMap(([, raw]) => {
          const valueObj = (raw || {}) as AIEnrichedValue;
          const citations = Array.isArray(valueObj.citations)
            ? (valueObj.citations as AICitationsItem[])
            : [];
          return citations
            .map((c) => c.id)
            .filter(
              (id): id is string => typeof id === "string" && id.includes("_"),
            );
        }),
      ),
    );
  }, [aiFieldEntries, isOpen]);

  // Hydrate entities via TypeWire API client
  const entitiesApi = useTypeWire(EntitiesAPIWire);
  const { data: hydrated } = useQuery<{
    entities: Record<string, Entity>;
  } | null>({
    queryKey: ["entities-by-ids", allCitationIds.join(",")],
    queryFn: async () => {
      if (allCitationIds.length === 0) return { entities: {} };
      return entitiesApi.fetchByIds(allCitationIds);
    },
    enabled: allCitationIds.length > 0 && isOpen, // Only fetch when sheet is open
  });

  const { data: externalConnections } = useExternalConnectionLinks();

  // Track per-field evidence expansion state
  const [evidenceExpanded, setEvidenceExpanded] = useState<
    Record<string, boolean>
  >({});
  const toggleEvidence = useCallback(
    (fieldId: string) =>
      setEvidenceExpanded((prev) => ({ ...prev, [fieldId]: !prev[fieldId] })),
    [],
  );

  // Reset evidence expansion state when sheet closes
  useEffect(() => {
    if (!isOpen) {
      setEvidenceExpanded({});
    }
  }, [isOpen]);

  // Early return after all hooks for performance
  if (!isOpen) {
    return null;
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        className="bg-card !duration-150" // Override default animation duration for faster close
        style={{
          width: "var(--dr-details-width, 28rem)",
          maxWidth: "min(1200px, 95vw)",
        }}
      >
        {/* Resize handle */}
        <div
          aria-label="Resize details"
          className="absolute left-0 top-0 h-full w-1 cursor-col-resize select-none"
          onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.clientX;
            const sheet =
              (e.currentTarget.parentElement as HTMLElement) || null;
            // Compute starting width from computed style or fallback to current offsetWidth
            const startWidth = sheet ? sheet.offsetWidth : 448; // ~28rem
            const onMove = (ev: MouseEvent) => {
              const dx = startX - ev.clientX; // dragging left increases width
              const newWidth = Math.min(Math.max(startWidth + dx, 320), 1200); // clamp 20rem..75rem
              // Direct update for immediate responsiveness
              document.documentElement.style.setProperty(
                "--dr-details-width",
                `${newWidth}px`,
              );
            };
            const onUp = () => {
              cleanupResizeListeners();
            };
            // Save refs for cleanup (including if unmounted mid-drag)
            moveHandlerRef.current = onMove;
            upHandlerRef.current = onUp;
            isDraggingRef.current = true;
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp, { once: true });
          }}
        />
        <SheetHeader className="pb-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-xl font-semibold tracking-tight">
              {title}
            </SheetTitle>
          </div>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-8rem)] mt-6 pr-3">
          <div className="space-y-8">
            {/* Primary details */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div className="text-muted-foreground">Stage</div>
                <div className="text-foreground/90 font-medium">
                  {(data.stage_name as string) || "N/A"}
                </div>
                <div className="text-muted-foreground">Type</div>
                <div className="text-foreground/90 font-medium">
                  {(data.type as string) || "N/A"}
                </div>
                <div className="text-muted-foreground">Amount</div>
                <div className="text-foreground/90 font-medium">
                  {typeof data.total_amount === "number"
                    ? (data.total_amount as number).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : "N/A"}
                </div>
                <div className="text-muted-foreground">Close Date</div>
                <div className="text-foreground/90 font-medium">
                  {data.close_date
                    ? formatDate(String(data.close_date))
                    : "N/A"}
                </div>
                <div className="text-muted-foreground">Account</div>
                <div className="text-foreground/90 font-medium">
                  {(data.account_name as string) || "N/A"}
                </div>
                {externalRecord && (
                  <>
                    <div className="text-muted-foreground">Source ID</div>
                    <div className="text-foreground/90 font-medium">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a
                            href={
                              getExternalConnectionLink(externalRecord) ||
                              undefined
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-foreground/80 hover:bg-muted py-1 px-2 rounded-md transition-colors -mr-2 inline-flex items-center gap-1 border"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span>{externalRecord.source_record_id}</span>
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            View in{" "}
                            {getExternalSystemName(
                              externalRecord.source_record_type,
                            )}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* AI fields sections */}
            {aiFieldEntries.length > 0 && (
              <div className="space-y-8">
                {aiFieldEntries.map(([key, raw]) => {
                  const fieldId = key;
                  const fieldTitle = aiFieldIdToDisplayName.get(fieldId) || key;
                  const valueObj = (raw || {}) as AIEnrichedValue;
                  const displayValue = formatAIValue(valueObj);
                  const citations = Array.isArray(valueObj.citations)
                    ? (valueObj.citations as AICitationsItem[])
                    : [];
                  const isExpanded = !!evidenceExpanded[fieldId];

                  return (
                    <div key={key} className="space-y-3">
                      <h3 className="text-sm font-semibold text-foreground/90 tracking-tight flex items-center gap-2">
                        {fieldTitle}
                      </h3>
                      <div
                        className={cn(
                          "text-[13px] whitespace-pre-wrap leading-6",
                          "bg-muted/40 p-3 rounded-md border",
                        )}
                      >
                        {displayValue}
                      </div>

                      {/* Evidence summary + toggle */}
                      {citations.length > 0 && (
                        <div className="space-y-2">
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                            onClick={() => toggleEvidence(fieldId)}
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <span>Evidence ({citations.length})</span>
                          </button>

                          {/* Lazy render citations only when expanded */}
                          {isExpanded && (
                            <div className="space-y-2 mt-1">
                              {/* Use virtualized list for better performance */}
                              {citations.length > 10 ? (
                                <VirtualizedCitationList
                                  citations={citations}
                                  entities={hydrated?.entities || {}}
                                  externalConnections={externalConnections}
                                  fieldId={fieldId}
                                />
                              ) : (
                                // For fewer citations, render all directly
                                citations.map((c, idx) => (
                                  <CitationCard
                                    key={`${fieldId}-${idx}`}
                                    citation={c}
                                    entity={hydrated?.entities?.[c.id || ""]}
                                    externalConnections={externalConnections}
                                  />
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* System id */}
            {row?.entity_id && (
              <div className="flex justify-end">
                <SystemId id={row.entity_id} />
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

export function DeepResearchDetailsSheet(props: DeepResearchDetailsSheetProps) {
  // Don't render anything if not open to avoid unnecessary computation
  if (!props.isOpen) return null;
  return <DeepResearchDetailsSheetContent {...props} />;
}
